import React, { useEffect, useState } from 'react';
import { Drawer, DrawerContent, DrawerTrigger } from 'components/ui/drawer';
import { MoreHorizontal } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/ui/collapsible';
import { Button } from 'components/ui/button';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useOrder } from 'hooks/api/scheduling/useOrder';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCancelationReason } from 'hooks/api/general';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';

export type DataTableDrawersProps = {
  trigger?: React.ReactNode;
  table?: any;
  asChild?: boolean;
};

interface UpdateOrderStatusProps {
  ids: number[];
  status: string;
}

export const PendingOrdersDrawer: React.FC<DataTableDrawersProps> = ({
  trigger,
  table,
  asChild,
}) => {
  const [open, setOpen] = useState(false);
  const { updateOrderStatus } = useOrder();
  const queryClient = useQueryClient();
  const { getCancelationReasons } = useCancelationReason();
  const [cancelationReason, setCancelationReason] = useState('' as string);

  const {
    isPending,
    error,
    data: cancelationReasons,
  } = useQuery({
    queryKey: ['getCancelationReasons'],
    queryFn: () => getCancelationReasons(),
  });

  const mutation = useMutation({
    mutationFn: (newData: UpdateOrderStatusProps) =>
      updateOrderStatus(newData.ids, newData.status, cancelationReason),
    onSuccess: (data, variables) => {
      if (variables.status === 'approved') {
        toast('Solicitações aprovadas com sucesso');
      } else if (variables.status === 'rejected') {
        toast('Solicitações rejeitadas com sucesso');
      }

      queryClient.invalidateQueries({ queryKey: ['getPendingOrders'] });
      table.toggleAllPageRowsSelected(false);
      setOpen(false);
    },
    onError: (error) => {
      toast('Erro ao aprovar/reprovar solicitações', {
        description: error.message,
      });
    },
  });

  const handleOrderStatus = (status: string) => {
    const ids = table
      .getFilteredSelectedRowModel()
      .rows.map((row: any) => row.original.id);
    mutation.mutate({ ids, status });
  };

  return (
    <>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild={asChild}>
          {trigger || <MoreHorizontal className="h-5 w-5 text-slate-500" />}
        </DrawerTrigger>
        <DrawerContent className="text-center">
          <>
            <div>
              <span
                className={`inline-block mb-4 bg-slate-200 text-slate-500 p-2 rounded text-xs font-bold`}
              >
                Aprovar/Reprovar
              </span>
              <div className="w-[940px] m-auto mt-6 border-b borber-b-slate-600">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">ID</TableHead>
                      <TableHead>Doca</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Transportadora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Itens</TableHead>
                      <TableHead>Qtd</TableHead>
                      <TableHead>Volume</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {table
                      .getFilteredSelectedRowModel()
                      .rows.map((row: any) => (
                        <Collapsible key={row.original.id} asChild>
                          <>
                            <TableRow key={row.original.id}>
                              <TableCell className="font-medium">
                                {row.original.id}
                              </TableCell>
                              <TableCell>{row.original.dock.name}</TableCell>
                              <TableCell>
                                {format(
                                  new Date(row.original.scheduledAt),
                                  'dd/MM/yyyy HH:mm',
                                  { locale: ptBR }
                                )}
                              </TableCell>
                              <TableCell>
                                {row.original.carrier.tradeName}
                              </TableCell>
                              <TableCell>
                                {row.original.orderType.name}
                              </TableCell>
                              <TableCell>
                                {row.original.orderItems.length}
                              </TableCell>
                              <TableCell>
                                {row.original.orderItems
                                  .reduce(
                                    (acc: number, item: any) =>
                                      acc + item.quantity,
                                    0
                                  )
                                  .toLocaleString('pt-BR', {
                                    minimumFractionDigits: 2,
                                  })}
                              </TableCell>
                              <TableCell>
                                {row.original.orderItems
                                  .reduce(
                                    (acc: number, item: any) =>
                                      acc + item.volume,
                                    0
                                  )
                                  .toLocaleString('pt-BR', {
                                    minimumFractionDigits: 2,
                                  })}
                              </TableCell>
                              <TableCell>
                                <CollapsibleTrigger asChild>
                                  <MoreHorizontal className="h-5 w-5 text-slate-500 cursor-pointer" />
                                </CollapsibleTrigger>
                              </TableCell>
                            </TableRow>
                            <CollapsibleContent asChild>
                              <TableRow>
                                <TableCell colSpan={9} className="p-0">
                                  <div className="bg-gray-100 p-4">
                                    <div className="grid grid-cols-9 gap-4 font-semibold border-b border-b-gray-300 py-2 mb-2">
                                      <div>Código</div>
                                      <div className="col-span-5">
                                        Descrição
                                      </div>
                                      <div>Quantidade</div>
                                      <div>Volume</div>
                                      <div>Unidade</div>
                                      <div>Lote</div>
                                    </div>
                                    {row.original.orderItems.map(
                                      (item: any) => (
                                        <div
                                          key={item.id}
                                          className="grid grid-cols-9 gap-4 border-b border-dashed border-b-gray-300 pt-2 pb-4 mb-2 last:border-b-0 last:pb-0"
                                        >
                                          <div>{item.code}</div>
                                          <div className="col-span-5">
                                            {item.description}
                                          </div>
                                          <div>{item.quantity}</div>
                                          <div>{item.volume}</div>
                                          <div>{item.unit}</div>
                                          <div>{item.batch}</div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            </CollapsibleContent>
                          </>
                        </Collapsible>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="w-[940px] m-auto flex gap-2">
              <Button
                size="default"
                className="w-full bg-emerald-600 text-slate-100 m-auto mt-4 font-GilroySemibold text-sm p-6 flex gap-2 hover:bg-emerald-800 transition-all"
                onClick={() => handleOrderStatus('approved')}
                disabled={mutation.isPending}
              >
                Aprovar
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    size="default"
                    className="w-full bg-red-600 text-slate-100 m-auto mt-4 font-GilroySemibold text-sm p-6 flex gap-2 hover:bg-red-800 transition-all"
                    // onClick={() => handleOrderStatus('rejected')}
                    disabled={mutation.isPending}
                  >
                    Reprovar
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Selecione o motivo da reprovação</DialogTitle>
                    <DialogDescription>
                      <Select onValueChange={setCancelationReason}>
                        <SelectTrigger className="w-full mt-2 border-solid focus:ring-primary-500 text-slate-500 font-sans">
                          <SelectValue placeholder="Selecione o motivo da repovação" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel></SelectLabel>
                            {cancelationReasons?.map((reason: any) => (
                              <SelectItem
                                key={reason.value}
                                value={reason.value.toString()}
                              >
                                {reason.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <Button
                        size="default"
                        className="w-full bg-red-600 text-slate-100 m-auto mt-4 font-GilroySemibold text-sm p-6 flex gap-2 hover:bg-red-800 transition-all"
                        onClick={() => handleOrderStatus('rejected')}
                        disabled={mutation.isPending || !cancelationReason}
                      >
                        Reprovar
                      </Button>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>
          </>
        </DrawerContent>
      </Drawer>
    </>
  );
};
